import type { GetSessionSnapshotResponse } from "@nlc/shared-types";

import { parseFileName } from "@/utils/getExtensionFromMime.ts";

import type { FileResultStatus } from "../types";
import type { FileVersionMetadata } from "../types/fileVersionMetadata";
import type {
  InFlightUpload,
  SessionFileRow,
  SessionFileStatus,
} from "../types/sessionFile";

import {
  mapAttachmentToFileResult,
  resolveAttachmentFile,
} from "./mapEvaluation";

interface MergeSessionFilesInput {
  fileMetadata: Map<string, FileVersionMetadata>;
  inFlightUploads: InFlightUpload[];
  localFiles: Map<number, File>;
  snapshot: GetSessionSnapshotResponse | undefined;
}

/**
 * Merges server snapshot attachments with client-side in-flight uploads so every
 * step reads the same file list.
 */
export function mergeSessionFiles({
  fileMetadata,
  inFlightUploads,
  localFiles,
  snapshot,
}: MergeSessionFilesInput): SessionFileRow[] {
  const liveSnapshotEntries = snapshot?.attachments ?? [];

  const inFlightByAttachmentId = new Map<number, InFlightUpload>();
  const inFlightByReplaceTarget = new Map<number, InFlightUpload>();
  const inFlightWithoutAttachment: InFlightUpload[] = [];

  for (const upload of inFlightUploads) {
    if (upload.replacesAttachmentId !== undefined) {
      inFlightByReplaceTarget.set(upload.replacesAttachmentId, upload);
    }

    if (upload.attachmentId === undefined) {
      if (upload.replacesAttachmentId === undefined) {
        inFlightWithoutAttachment.push(upload);
      }
      continue;
    }

    inFlightByAttachmentId.set(upload.attachmentId, upload);
  }

  const rows: SessionFileRow[] = [];
  const consumedAttachmentIds = new Set<number>();

  for (const upload of inFlightWithoutAttachment) {
    rows.push(inFlightUploadToRow(upload, fileMetadata));
  }

  for (const entry of liveSnapshotEntries) {
    const attachmentId = entry.attachment.id;
    const replaceInFlight = inFlightByReplaceTarget.get(attachmentId);
    const attachInFlight = inFlightByAttachmentId.get(attachmentId);

    consumedAttachmentIds.add(attachmentId);

    if (replaceInFlight && isActiveInFlight(replaceInFlight)) {
      rows.push(
        mergeReplaceWithSnapshot({
          entry,
          inFlight: replaceInFlight,
          localFiles,
        }),
      );
      continue;
    }

    if (attachInFlight && isActiveInFlight(attachInFlight)) {
      rows.push(
        mergeSnapshotWithInFlight({
          entry,
          fileMetadata,
          inFlight: attachInFlight,
          localFiles,
        }),
      );
      continue;
    }

    rows.push(snapshotEntryToRow({ entry, fileMetadata, localFiles }));
  }

  for (const [attachmentId, inFlight] of inFlightByAttachmentId) {
    if (consumedAttachmentIds.has(attachmentId)) continue;

    rows.push(
      inFlightUploadToRow(
        {
          ...inFlight,
          attachmentId,
        },
        fileMetadata,
      ),
    );
  }

  return rows.sort((a, b) => a.clientId.localeCompare(b.clientId));
}

function isActiveInFlight(upload: InFlightUpload): boolean {
  return (
    upload.status === "uploading" ||
    upload.status === "attaching" ||
    upload.status === "error"
  );
}

function inFlightUploadToRow(
  upload: InFlightUpload,
  fileMetadata: Map<string, FileVersionMetadata>,
): SessionFileRow {
  const metadata = fileMetadata.get(upload.clientId);

  return {
    attachmentId: upload.attachmentId ?? null,
    clientId: upload.clientId,
    displayBaseName: resolveDisplayBaseName({
      defaultName: upload.file.name,
      metadata,
    }),
    displayName: resolveDisplayName({
      defaultName: upload.file.name,
      metadata,
    }),
    error: upload.error,
    evaluationId: null,
    file: upload.file,
    mime: upload.file.type,
    previousVersionId:
      metadata?.previousVersionId ?? upload.previousVersionId ?? null,
    progress: upload.progress,
    replacesAttachmentId: upload.replacesAttachmentId,
    score: null,
    status: mapInFlightStatus(upload.status),
    tag: null,
  };
}

function mergeReplaceWithSnapshot({
  entry,
  inFlight,
  localFiles,
}: {
  entry: GetSessionSnapshotResponse["attachments"][number];
  inFlight: InFlightUpload;
  localFiles: Map<number, File>;
}): SessionFileRow {
  const fileResult = mapAttachmentToFileResult({
    attachment: entry.attachment,
    evaluation: entry.evaluation,
    localFile: localFiles.get(entry.attachment.id) ?? inFlight.file,
  });

  return {
    attachmentId: entry.attachment.id,
    clientId: inFlight.clientId,
    displayBaseName: resolveDisplayBaseName({
      defaultName: entry.attachment.name,
      metadata: undefined,
    }),
    displayName: entry.attachment.name,
    error: inFlight.error,
    evaluationId: fileResult.evaluationId,
    file: inFlight.file,
    mime: entry.attachment.mime || inFlight.file.type,
    previousVersionId: inFlight.previousVersionId ?? null,
    progress: inFlight.progress,
    replacesAttachmentId: inFlight.replacesAttachmentId,
    score: fileResult.score,
    status: mapReplaceInFlightStatus(inFlight.status),
    tag: fileResult.tag,
  };
}

function mergeSnapshotWithInFlight({
  entry,
  fileMetadata,
  inFlight,
  localFiles,
}: {
  entry: GetSessionSnapshotResponse["attachments"][number];
  fileMetadata: Map<string, FileVersionMetadata>;
  inFlight: InFlightUpload;
  localFiles: Map<number, File>;
}): SessionFileRow {
  const fileResult = mapAttachmentToFileResult({
    attachment: entry.attachment,
    evaluation: entry.evaluation,
    localFile: localFiles.get(entry.attachment.id) ?? inFlight.file,
  });
  const metadata =
    fileMetadata.get(inFlight.clientId) ??
    fileMetadata.get(String(entry.attachment.id));

  return {
    attachmentId: entry.attachment.id,
    clientId: inFlight.clientId,
    displayBaseName: resolveDisplayBaseName({
      defaultName: entry.attachment.name,
      metadata,
    }),
    displayName: resolveDisplayName({
      defaultName: entry.attachment.name,
      metadata,
    }),
    error: inFlight.error,
    evaluationId: fileResult.evaluationId,
    file: inFlight.file,
    mime: entry.attachment.mime || inFlight.file.type,
    previousVersionId:
      metadata?.previousVersionId ?? inFlight.previousVersionId ?? null,
    progress: inFlight.progress,
    score: fileResult.score,
    status: mapInFlightStatus(inFlight.status),
    tag: fileResult.tag,
  };
}

function snapshotEntryToRow({
  entry,
  fileMetadata,
  localFiles,
}: {
  entry: GetSessionSnapshotResponse["attachments"][number];
  fileMetadata: Map<string, FileVersionMetadata>;
  localFiles: Map<number, File>;
}): SessionFileRow {
  const fileResult = mapAttachmentToFileResult({
    attachment: entry.attachment,
    evaluation: entry.evaluation,
    localFile: localFiles.get(entry.attachment.id) ?? null,
  });
  const clientId = String(entry.attachment.id);
  const metadata = fileMetadata.get(clientId);

  return {
    attachmentId: entry.attachment.id,
    clientId,
    displayBaseName: resolveDisplayBaseName({
      defaultName: entry.attachment.name,
      metadata,
    }),
    displayName: resolveDisplayName({
      defaultName: entry.attachment.name,
      metadata,
    }),
    evaluationId: fileResult.evaluationId,
    file: resolveAttachmentFile(entry.attachment, localFiles),
    mime: entry.attachment.mime || fileResult.file.type,
    previousVersionId: metadata?.previousVersionId ?? null,
    score: fileResult.score,
    status: mapEvaluationStatus(fileResult.status),
    tag: fileResult.tag,
  };
}

function resolveDisplayName({
  defaultName,
  metadata,
}: {
  defaultName: string;
  metadata: FileVersionMetadata | undefined;
}): string {
  if (!metadata) return defaultName;

  if (metadata.displayBaseName.trim() === "") return defaultName;

  const extension = defaultName.includes(".")
    ? defaultName.split(".").pop()
    : null;

  if (!extension) return metadata.displayBaseName;

  return `${metadata.displayBaseName}.${extension}`;
}

function resolveDisplayBaseName({
  defaultName,
  metadata,
}: {
  defaultName: string;
  metadata: FileVersionMetadata | undefined;
}): string {
  if (metadata) return metadata.displayBaseName;

  try {
    return parseFileName(defaultName).baseName;
  } catch {
    return defaultName;
  }
}

function mapReplaceInFlightStatus(
  status: InFlightUpload["status"],
): SessionFileStatus {
  switch (status) {
    case "uploading": {
      return "replacing";
    }
    case "attaching": {
      return "replacing";
    }
    case "error": {
      return "error";
    }
    default: {
      return "replacing";
    }
  }
}

function mapInFlightStatus(
  status: InFlightUpload["status"],
): SessionFileStatus {
  switch (status) {
    case "uploading": {
      return "uploading";
    }
    case "attaching": {
      return "attaching";
    }
    case "error": {
      return "error";
    }
    case "complete": {
      return "evaluating";
    }
    default: {
      return "uploading";
    }
  }
}

function mapEvaluationStatus(status: FileResultStatus): SessionFileStatus {
  switch (status) {
    case "loading": {
      return "evaluating";
    }
    case "done": {
      return "ready";
    }
    case "pending": {
      return "evaluating";
    }
    default: {
      return "evaluating";
    }
  }
}
